import { Chips, NumberInput, Separator, Slider } from 'platform/components';
import { HStack, VStack } from 'platform/foundation-tw';
import { useState } from 'react';
import { isNil } from 'ramda';
import { isArray, isString } from 'ramda-adjunct';
import { suffixTestId, type Nullish } from 'shared/web';
import { useFilterOnChipsChange } from '../../hooks/useFilterOnChipsChange';
import { useSliderTicks } from '../../hooks/useSliderTicks';
import type { EagFilterProps } from '../../types/EagFilterProps';
import { useSliderFilter } from './hooks/useSliderFilter';
export type NumberSliderRangeProps = EagFilterProps & {
  min: number;
  max: number;
  minOpen: boolean;
  maxOpen: boolean;
  unit: string | null;
  stepSize: number | null;
  steps: number[];
  markerSize: number | null;
  markers: number[];
  decimalsAllowed: boolean;
  inputsVisible: boolean;
  quickFilters?: Array<{
    value: string;
    label: string;
  }>;
};
const FILTER_VALUE_KEY = 'value';
export function NumberSliderRangeFilter(props: NumberSliderRangeProps) {
  const ticks = useSliderTicks(props.min, props.max, props.markerSize, props.markers);
  const model = props.value[FILTER_VALUE_KEY] as [number | null, number | null] | string | Nullish;
  const valueFormatter = (value: number) => props.unit ? `${value} [${props.unit}]` : `${value}`;
  const [localSliderValue, setLocalSliderValue] = useState<[number | null, number | null] | null>([props.min, props.max]);
  const onModelChange = (newValue: unknown) => {
    props.onChange({
      [FILTER_VALUE_KEY]: newValue
    });
  };
  const [filterValue, setFilterValue] = useSliderFilter({
    model,
    onModelChange,
    min: props.min,
    max: props.max
  });
  const {
    onChipsChange
  } = useFilterOnChipsChange({
    setFilterValue: newValue => {
      setFilterValue(newValue);
      setLocalSliderValue([props.min, props.max]);
    },
    defaultValue: null
  });
  const onSliderValueChange = (value: [number | null, number | null] | null) => {
    setLocalSliderValue(value);
    setFilterValue(value);
  };
  const onFromInputChange = (value: number | null) => {
    const valueToSet = value === null || isNaN(value) ? null : value;
    if (isNil(filterValue)) {
      onSliderValueChange([valueToSet, null]);
    }
    if (isArray(filterValue)) {
      const isFromNilOrLowestPossible = isNil(valueToSet) || valueToSet === props.min;
      const isToNilOrLowestPossible = isNil(filterValue?.[1]) || filterValue?.[1] === props.max;
      if (isFromNilOrLowestPossible && isToNilOrLowestPossible) {
        onSliderValueChange(null);
      } else {
        onSliderValueChange([valueToSet, filterValue?.[1] === 0 ? null : filterValue?.[1]]);
      }
    }
    if (isString(filterValue)) {
      onSliderValueChange([valueToSet, props.max]);
    }
  };
  const onToInputChange = (value: number | null) => {
    const valueToSet = value === null || isNaN(value) ? null : value;
    if (isNil(filterValue)) {
      onSliderValueChange([null, valueToSet]);
    }
    if (isArray(filterValue)) {
      const isFromNilOrLowestPossible = isNil(filterValue?.[0]) || filterValue?.[0] === props.min;
      const isToNilOrLowestPossible = isNil(valueToSet) || valueToSet === props.max;
      if (isFromNilOrLowestPossible && isToNilOrLowestPossible) {
        onSliderValueChange(null);
      } else {
        onSliderValueChange([filterValue?.[0] === 0 ? null : filterValue?.[0], valueToSet]);
      }
    }
    if (isString(filterValue)) {
      onSliderValueChange([props.min, valueToSet]);
    }
  };
  const isQuickFilterValue = isString(model) ? [model] : null;
  const isSliderValue = isArray(localSliderValue) ? [localSliderValue?.[0] ?? props.min, localSliderValue?.[1] ?? props.max] : [props.min, props.max];
  const isFromValue = isArray(filterValue) ? filterValue?.[0] : null;
  const isToValue = isArray(filterValue) ? filterValue?.[1] : null;
  return <VStack spacing={5}>
      {props.quickFilters && props.quickFilters.length > 0 && <>
          <HStack>
            <Chips isDisabled={props.isDisabled} value={isQuickFilterValue} options={props.quickFilters} onChange={onChipsChange} isMultiple={false} data-testid={suffixTestId('number-range-filter-quickFilters', props)} isDeselectable />
          </HStack>
          <Separator />
        </>}
      <Slider isDisabled={props.isDisabled} isRange min={props.min} max={props.max} step={props.stepSize ?? props.steps ?? 1} value={isSliderValue as [number, number]} allowMoveStart onChange={onSliderValueChange} formatValue={valueFormatter} ticks={ticks} onlyEdges={true} displayValue="auto" data-testid={suffixTestId('number-range-filter-slider', props)} />
      <HStack spacing={2}>
        <NumberInput value={isFromValue} onChange={onFromInputChange} isStepperVisible minStepperValue={props.min} maxStepperValue={props.max} isDisabled={props.isDisabled} data-testid={suffixTestId('number-range-filter-from', props)} />
        <NumberInput value={isToValue} onChange={onToInputChange} isStepperVisible minStepperValue={props.min} maxStepperValue={props.max} isDisabled={props.isDisabled} data-testid={suffixTestId('number-range-filter-to', props)} />
      </HStack>
    </VStack>;
}